import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu, X, Heart, ChevronDown,
  FlaskConical, GraduationCap, Lightbulb, Handshake,
  BookOpen, Image, Award, Newspaper, Phone, Home, Info,
  BarChart3, TrendingUp
} from 'lucide-react';

const NAV = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/about', label: 'About', icon: Info },
  {
    label: 'Our Work', icon: FlaskConical,
    children: [
      { to: '/programs',                  label: 'All Programs & Initiatives', icon: BookOpen },
      // { to: '/programs?category=research',label: 'Research & Evaluation',      icon: FlaskConical },
      // { to: '/programs?category=capacity',label: 'Capacity Development',       icon: GraduationCap },
      // { to: '/programs?category=innovation',label:'Social Innovation',          icon: Lightbulb },
      { to: '/grants',                    label: 'Grants & Partnerships',       icon: Award },
    ],
  },
  {
    label: 'Impact & Data', icon: BarChart3,
    children: [
      { to: '/impact',   label: 'Impact Metrics Dashboard', icon: TrendingUp },
      { to: '/insights', label: 'Data & Insights Portal',   icon: BarChart3 },
    ],
  },
  { to: '/gallery',  label: 'Gallery',      icon: Image },
  { to: '/blog',     label: 'Publications', icon: Newspaper },
  { to: '/contact',  label: 'Contact',      icon: Phone },
];

export default function Navbar() {
  const [scrolled,  setScrolled]  = useState(false);
  const [open,      setOpen]      = useState(false);
  const [dropdown,  setDropdown]  = useState(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setDropdown(null); }, [location]);

  const isActive = (path) => location.pathname === path;
  const transparent = isHome && !scrolled;

  return (
    <>
      <nav className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        transparent
          ? 'bg-transparent border-b border-white/10'
          : 'bg-white/95 backdrop-blur-xl border-b border-gray-100 shadow-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0">
              <div className="relative">
                <img src="/logo.jpeg" alt="SAAI" className="h-10 w-10 rounded-full object-cover ring-2 ring-gold-500/40" />
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-gold-500 rounded-full border-2 border-white" />
              </div>
              <div className="leading-tight">
                <div className={`font-display font-bold text-base tracking-tight transition-colors ${transparent ? 'text-white' : 'text-royal-900'}`}>
                  SAAI Institute
                </div>
                <div className={`text-[10px] font-medium tracking-widest uppercase transition-colors ${transparent ? 'text-gold-400' : 'text-gold-600'}`}>
                  <span className="font-serif italic lowercase tracking-normal text-xs mr-1">of</span> Education & Capacity Development
                </div>
              </div>
            </Link>

            {/* Desktop nav */}
            <ul className="hidden lg:flex items-center gap-0.5">
              {NAV.map(item => (
                <li key={item.label} className="relative">
                  {item.children ? (
                    <div>
                      <button
                        onClick={() => setDropdown(dropdown === item.label ? null : item.label)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                          transparent
                            ? 'text-white/80 hover:text-white hover:bg-white/10'
                            : 'text-gray-600 hover:text-royal-800 hover:bg-royal-50'
                        }`}>
                        <item.icon size={14} />
                        {item.label}
                        <ChevronDown size={12} className={`transition-transform ${dropdown === item.label ? 'rotate-180' : ''}`} />
                      </button>
                      {dropdown === item.label && (
                        <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 animate-fade-in">
                          {item.children.map(c => (
                            <Link key={c.to} to={c.to}
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-royal-50 hover:text-royal-800 transition-colors">
                              {c.icon && <c.icon size={14} className="text-royal-600 flex-shrink-0" />}
                              {c.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link to={item.to}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                        isActive(item.to)
                          ? transparent ? 'text-gold-400 bg-white/10' : 'text-royal-800 bg-royal-50'
                          : transparent ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-royal-800 hover:bg-royal-50'
                      }`}>
                      <item.icon size={14} />
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/partner"
                className={`text-sm font-semibold transition-colors ${transparent ? 'text-white/80 hover:text-white' : 'text-royal-800 hover:text-royal-900'}`}>
                Partner
              </Link>
              <Link to="/donate" className="btn-primary text-sm py-2.5 px-5">
                <Heart size={15} className="fill-current" /> Support Us
              </Link>
            </div>

            {/* Hamburger */}
            <button onClick={() => setOpen(!open)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${transparent ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100'}`}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="fixed top-0 right-0 bottom-0 w-80 bg-white shadow-2xl flex flex-col animate-slide-right">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <img src="/logo.jpeg" alt="SAAI" className="h-8 w-8 rounded-full object-cover" />
                <div>
                  <div className="font-display font-bold text-royal-900 text-sm">SAAI Institute</div>
                  <div className="text-[10px] text-gold-600 uppercase tracking-widest">
                    <span className="font-serif italic lowercase tracking-normal text-xs mr-1">of</span> Education & Capacity Development</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={20} /></button>
            </div>

            <nav className="flex-1 overflow-y-auto p-4 space-y-1">
              {NAV.map(item => (
                item.children ? (
                  <div key={item.label}>
                    <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-widest mt-3">
                      <item.icon size={12} />{item.label}
                    </div>
                    {item.children.map(c => (
                      <Link key={c.to} to={c.to}
                        className="flex items-center gap-3 pl-7 pr-3 py-2 text-sm text-gray-700 hover:text-royal-800 hover:bg-royal-50 rounded-lg transition-colors">
                        {c.icon && <c.icon size={13} className="text-royal-600" />}{c.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link key={item.to} to={item.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive(item.to) ? 'bg-royal-800 text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}>
                    <item.icon size={16} />{item.label}
                  </Link>
                )
              ))}
              <Link to="/partner"
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">
                <Handshake size={16} />Partner With Us
              </Link>
            </nav>

            <div className="p-4 border-t border-gray-100">
              <Link to="/donate" className="btn-primary w-full justify-center">
                <Heart size={16} className="fill-current" /> Support SAAI
              </Link>
            </div>
          </div>
        </div>
      )}

      {dropdown && <div className="fixed inset-0 z-40" onClick={() => setDropdown(null)} />}
    </>
  );
}